/**
 * PanelManager - Handles UI panel interactions
 */

import { eventBus, GameEvents } from '../core/EventBus.js';
import { gameState } from '../core/GameState.js';
import { achievementSystem } from '../systems/AchievementSystem.js';
import { progressionSystem } from '../systems/ProgressionSystem.js';
import { tutorialSystem } from '../systems/TutorialSystem.js';
import { setMusicVolume, setSFXVolume, setMasterVolume, getVolumeSettings } from '../audio/AudioManager.js';

// Panel elements
let achievementsPanel = null;
let settingsPanel = null;
let statisticsModal = null;
let resetModal = null;
let progressionHUD = null;

// State
let currentTab = 'all';

/**
 * Initialize all panel interactions
 */
export function initPanels() {
  // Cache DOM elements
  achievementsPanel = document.getElementById('achievements-panel');
  settingsPanel = document.getElementById('settings-panel');
  statisticsModal = document.getElementById('stats-modal');
  resetModal = document.getElementById('reset-confirm-modal');
  progressionHUD = document.getElementById('progression-hud');

  // Set up button listeners
  setupButtonListeners();

  // Set up settings controls
  setupSettingsControls();

  // Set up achievement tabs
  setupAchievementTabs();

  // Set up event listeners for game events
  setupGameEventListeners();

  // Initial HUD update
  updateProgressionHUD();

  // Initial achievements population
  populateAchievements();
}

/**
 * Set up button click listeners
 */
function setupButtonListeners() {
  // Settings button
  const settingsBtn = document.getElementById('settings-button');
  if (settingsBtn) {
    settingsBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      togglePanel('settings');
    });
  }

  // Achievements button
  const achievementsBtn = document.getElementById('achievements-button');
  if (achievementsBtn) {
    achievementsBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      togglePanel('achievements');
    });
  }

  // Close buttons
  const closeSettingsBtn = document.getElementById('settings-close');
  if (closeSettingsBtn) {
    closeSettingsBtn.addEventListener('click', () => closePanel('settings'));
  }

  const closeAchievementsBtn = document.getElementById('achievements-close');
  if (closeAchievementsBtn) {
    closeAchievementsBtn.addEventListener('click', () => closePanel('achievements'));
  }

  // Statistics button
  const statisticsBtn = document.getElementById('view-stats');
  if (statisticsBtn) {
    statisticsBtn.addEventListener('click', openStatistics);
  }

  // Close statistics
  const closeStatisticsBtn = document.getElementById('stats-close');
  if (closeStatisticsBtn) {
    closeStatisticsBtn.addEventListener('click', closeStatistics);
  }

  // Reset progress button
  const resetProgressBtn = document.getElementById('reset-progress');
  if (resetProgressBtn) {
    resetProgressBtn.addEventListener('click', openResetConfirmation);
  }

  // Reset confirmation buttons
  const confirmResetBtn = document.getElementById('reset-confirm');
  if (confirmResetBtn) {
    confirmResetBtn.addEventListener('click', confirmReset);
  }

  const cancelResetBtn = document.getElementById('reset-cancel');
  if (cancelResetBtn) {
    cancelResetBtn.addEventListener('click', closeResetConfirmation);
  }

  // Click outside to close panels
  document.addEventListener('click', handleOutsideClick);

  // Keyboard shortcuts
  document.addEventListener('keydown', handleKeyboardShortcuts);
}

/**
 * Set up settings panel controls
 */
function setupSettingsControls() {
  // Get current audio settings
  const volumeSettings = getVolumeSettings();

  // Master volume slider
  const masterSlider = document.getElementById('master-volume');
  const masterValue = document.getElementById('master-volume-value');
  if (masterSlider && masterValue) {
    const savedMaster = Math.round(volumeSettings.master * 100);
    masterSlider.value = savedMaster;
    masterValue.textContent = `${savedMaster}%`;

    masterSlider.addEventListener('input', (e) => {
      const value = parseInt(e.target.value);
      masterValue.textContent = `${value}%`;
      setMasterVolume(value / 100);
    });
  }

  // Music volume slider
  const musicSlider = document.getElementById('music-volume');
  const musicValue = document.getElementById('music-volume-value');
  if (musicSlider && musicValue) {
    const savedMusic = Math.round(volumeSettings.music * 100);
    musicSlider.value = savedMusic;
    musicValue.textContent = `${savedMusic}%`;

    musicSlider.addEventListener('input', (e) => {
      const value = parseInt(e.target.value);
      musicValue.textContent = `${value}%`;
      setMusicVolume(value / 100);
    });
  }

  // SFX volume slider
  const sfxSlider = document.getElementById('sfx-volume');
  const sfxValue = document.getElementById('sfx-volume-value');
  if (sfxSlider && sfxValue) {
    const savedSfx = Math.round(volumeSettings.sfx * 100);
    sfxSlider.value = savedSfx;
    sfxValue.textContent = `${savedSfx}%`;

    sfxSlider.addEventListener('input', (e) => {
      const value = parseInt(e.target.value);
      sfxValue.textContent = `${value}%`;
      setSFXVolume(value / 100);
    });
  }

  // Restart tutorial button
  const restartTutorialBtn = document.getElementById('restart-tutorial');
  if (restartTutorialBtn) {
    restartTutorialBtn.addEventListener('click', () => {
      tutorialSystem?.reset?.();
      tutorialSystem?.start?.();
      closePanel('settings');
    });
  }
}

/**
 * Set up achievement tab switching
 */
function setupAchievementTabs() {
  const tabs = document.querySelectorAll('#achievements-tabs .tab-button');
  tabs.forEach(tab => {
    tab.addEventListener('click', () => {
      // Update active tab
      tabs.forEach(t => t.classList.remove('active'));
      tab.classList.add('active');

      // Filter achievements
      currentTab = tab.dataset.category || 'all';
      populateAchievements();
    });
  });
}

/**
 * Set up game event listeners
 */
function setupGameEventListeners() {
  // Update HUD on fish caught
  eventBus.on(GameEvents.FISH_CAUGHT, () => {
    updateProgressionHUD();
  });

  // Update achievements on unlock
  eventBus.on(GameEvents.ACHIEVEMENT_UNLOCKED, () => {
    populateAchievements();
    updateProgressionHUD();
  });

  // Update on area unlock
  eventBus.on(GameEvents.AREA_UNLOCKED, () => {
    updateProgressionHUD();
  });

  // Update on rod upgrade
  eventBus.on(GameEvents.ROD_UPGRADED, () => {
    updateProgressionHUD();
  });
}

/**
 * Toggle a panel open/closed
 */
export function togglePanel(panelName) {
  if (panelName === 'settings') {
    const isOpen = settingsPanel?.style.display === 'flex';
    closeAllPanels();
    if (!isOpen && settingsPanel) {
      settingsPanel.style.display = 'flex';
    }
  } else if (panelName === 'achievements') {
    const isOpen = achievementsPanel?.style.display === 'flex';
    closeAllPanels();
    if (!isOpen && achievementsPanel) {
      achievementsPanel.style.display = 'flex';
      populateAchievements();
    }
  }
}

/**
 * Close a specific panel
 */
export function closePanel(panelName) {
  if (panelName === 'settings' && settingsPanel) {
    settingsPanel.style.display = 'none';
  } else if (panelName === 'achievements' && achievementsPanel) {
    achievementsPanel.style.display = 'none';
  }
}

/**
 * Close all panels
 */
export function closeAllPanels() {
  if (settingsPanel) settingsPanel.style.display = 'none';
  if (achievementsPanel) achievementsPanel.style.display = 'none';
}

/**
 * Handle clicks outside panels to close them
 */
function handleOutsideClick(e) {
  // Don't close if clicking on a button that opens a panel
  if (e.target.closest('#settings-button') || e.target.closest('#achievements-button')) {
    return;
  }

  // Close settings if clicking outside
  if (settingsPanel?.style.display === 'flex' && !e.target.closest('#settings-panel')) {
    closePanel('settings');
  }

  // Close achievements if clicking outside
  if (achievementsPanel?.style.display === 'flex' && !e.target.closest('#achievements-panel')) {
    closePanel('achievements');
  }
}

/**
 * Handle keyboard shortcuts
 */
function handleKeyboardShortcuts(e) {
  // Escape to close panels
  if (e.key === 'Escape') {
    closeAllPanels();
    closeStatistics();
    closeResetConfirmation();
  }

  // A for achievements
  if (e.key === 'a' || e.key === 'A') {
    // Don't trigger if typing in an input
    if (e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA') {
      togglePanel('achievements');
    }
  }
}

/**
 * Update the progression HUD values
 */
export function updateProgressionHUD() {
  if (!progressionHUD) return;

  const state = gameState.getState();

  // Update rod level
  const rodLevelEl = document.getElementById('hud-rod-level');
  if (rodLevelEl) {
    const rodNames = ['Basic', 'Improved', 'Professional', 'Master'];
    const rodLevel = state.rodLevel || 1;
    rodLevelEl.textContent = rodNames[rodLevel - 1] || 'Basic';
  }

  // Update ponds unlocked
  const pondsEl = document.getElementById('hud-ponds-unlocked');
  if (pondsEl) {
    const unlockedPonds = state.unlockedPonds?.length || 4;
    pondsEl.textContent = `${unlockedPonds}/8`;
  }

  // Achievements unlocked
  const achievementsCountEl = document.getElementById('hud-achievements-count');
  if (achievementsCountEl) {
    const progress = achievementSystem?.getProgress?.() || { unlocked: 0, total: 18 };
    achievementsCountEl.textContent = `${progress.unlocked}/${progress.total}`;
  }
}

/**
 * Populate achievements panel
 */
export function populateAchievements() {
  const grid = document.getElementById('achievements-content');
  if (!grid) return;

  // Get achievements from system
  const allAchievements = achievementSystem?.achievements || [];
  const progress = achievementSystem?.getProgress?.() || { unlocked: 0, total: allAchievements.length };

  // Filter by current tab
  let filtered = allAchievements;
  if (currentTab !== 'all') {
    filtered = allAchievements.filter(a => a.category === currentTab);
  }

  // Update progress bar
  const progressFill = document.getElementById('achievements-progress-bar');
  const progressText = document.getElementById('achievements-progress-text');

  if (progressFill) {
    progressFill.style.width = `${progress.percentage || 0}%`;
  }
  if (progressText) {
    progressText.textContent = `${progress.unlocked}/${progress.total} Unlocked`;
  }

  // Clear and populate grid
  grid.innerHTML = '';

  filtered.forEach(achievement => {
    const isUnlocked = achievementSystem?.isUnlocked?.(achievement.id) || false;
    const card = createAchievementCard(achievement, isUnlocked);
    grid.appendChild(card);
  });
}

/**
 * Create an achievement card element
 */
function createAchievementCard(achievement, isUnlocked) {
  const card = document.createElement('div');
  card.className = `achievement-card ${isUnlocked ? 'unlocked' : 'locked'}`;

  // Icon based on category
  const icons = {
    catching: '🎣',
    rarity: '✨',
    collection: '📚',
    skill: '🏆',
    exploration: '🗺️'
  };

  card.innerHTML = `
    <div class="achievement-icon">${isUnlocked ? icons[achievement.category] || '🏅' : '🔒'}</div>
    <div class="achievement-info">
      <div class="achievement-name">${isUnlocked ? achievement.name : '???'}</div>
      <div class="achievement-description">${isUnlocked ? achievement.description : 'Keep playing to unlock!'}</div>
    </div>
  `;

  return card;
}

/**
 * Open statistics modal
 */
function openStatistics() {
  if (!statisticsModal) return;

  // Get stats from game state and existing DOM
  const state = gameState.getState();
  const inventory = state.inventory || {};
  const inventoryEntries = Object.values(inventory);

  // Calculate stats
  const totalFish = inventoryEntries.reduce((sum, entry) => sum + (entry.count || 0), 0);
  const discoveredEntries = inventoryEntries.filter(entry => entry.discovered);
  const uniqueSpecies = discoveredEntries.length;

  // Count discovered species by rarity
  const rarityDiscovered = { common: 0, uncommon: 0, rare: 0, legendary: 0 };
  const rarityTotals = { common: 6, uncommon: 6, rare: 5, legendary: 1 };

  discoveredEntries.forEach(entry => {
    const rarity = entry.fish?.rarity?.toLowerCase() || 'common';
    if (rarityDiscovered[rarity] !== undefined) {
      rarityDiscovered[rarity]++;
    }
  });

  // Get score from game display
  const gameFishCountEl = document.getElementById('fish-count');
  let score = 0;
  if (gameFishCountEl) {
    const match = gameFishCountEl.textContent.match(/Score: (\d+)/);
    if (match) score = parseInt(match[1]);
  }

  // Update stat cards
  const statElements = {
    'stats-total-caught': totalFish,
    'stats-species': `${uniqueSpecies}/18`,
    'stats-score': score,
    'stats-achievements': `${achievementSystem?.getProgress?.()?.unlocked || 0}/18`,
    'stats-rod': ['Basic', 'Improved', 'Professional', 'Master'][state.rodLevel - 1] || 'Basic',
    'stats-ponds': `${state.unlockedPonds?.length || 4}/8`
  };

  Object.entries(statElements).forEach(([id, value]) => {
    const el = document.getElementById(id);
    if (el) el.textContent = value;
  });

  // Update rarity bars
  Object.keys(rarityDiscovered).forEach(rarity => {
    const barEl = document.getElementById(`stats-${rarity}-bar`);
    const countEl = document.getElementById(`stats-${rarity}-count`);
    const discovered = rarityDiscovered[rarity];
    const total = rarityTotals[rarity];

    if (barEl) barEl.style.width = `${(discovered / total) * 100}%`;
    if (countEl) countEl.textContent = `${discovered}/${total}`;
  });

  statisticsModal.style.display = 'flex';
}

/**
 * Close statistics modal
 */
function closeStatistics() {
  if (statisticsModal) {
    statisticsModal.style.display = 'none';
  }
}

/**
 * Open reset confirmation modal
 */
function openResetConfirmation() {
  if (resetModal) {
    resetModal.style.display = 'flex';
  }
}

/**
 * Close reset confirmation modal
 */
function closeResetConfirmation() {
  if (resetModal) {
    resetModal.style.display = 'none';
  }
}

/**
 * Confirm and execute reset
 */
function confirmReset() {
  // Clear all localStorage
  localStorage.clear();

  // Reload the page to reset everything
  window.location.reload();
}

/**
 * Check if any panel is open
 */
export function isAnyPanelOpen() {
  return settingsPanel?.style.display === 'flex' ||
         achievementsPanel?.style.display === 'flex' ||
         statisticsModal?.style.display === 'flex' ||
         resetModal?.style.display === 'flex';
}

export default {
  initPanels,
  togglePanel,
  closePanel,
  closeAllPanels,
  updateProgressionHUD,
  populateAchievements,
  isAnyPanelOpen
};
